/**
 * Prüfung eines Etikett-Designs beim Speichern mit deutschen Sätzen je Pfad (Story 27.2). Grundlage ist das gemeinsame
 * Zod-Schema `labelDesignSchema` aus `design.ts` (Vertrag mit dem Render-Kern 27.3, dort nicht geändert); hier kommen die
 * engeren Regeln des Speicherns dazu:
 * - strikt: unbekannte Angaben (auch in Elementen und Bausteinen) werden abgelehnt statt still verworfen,
 * - Größe in den Grenzen der Einstellungen (`LabelDesignLimits`, versioniert in `settings`, Eingabe – keine Konstante) in
 *   Schritten von 0,1 mm und gleich der Vorlage, höchstens so viele Elemente wie eingestellt, eindeutige Element-Kennungen,
 *   jedes Element innerhalb des Etiketts,
 * - Feldschlüssel der Form `modul.feld`; `system.*` nur `user|company|web`, `receipt.*` nur bei Artikel-Etiketten.
 * Ob ein Listen-Feld wirklich existiert, prüft der Server gegen den Feld-Katalog (`unknownLabelFieldErrors`). Rein,
 * browser-sicher.
 */
import type { z } from 'zod';
import { LABEL_RECEIPT_FIELDS, LABEL_SYSTEM_FIELDS, LABEL_TEXTS } from './texts.ts';
import { labelDesignSchema, labelElementSchema, labelSegmentSchema, labelSheetLayoutSchema, type LabelDesign, type LabelSegment, type LabelSheetLayout } from './design.ts';

export type LabelTemplateKind = 'article' | 'bin';
export type LabelCheckResult<T> = { ok: true; value: T } | { ok: false; errors: Record<string, string> };

/**
 * Grenzen beim Speichern (enger als das gemeinsame Schema): kommen als Eingabe aus den versionierten Einstellungen
 * `label_size_min_mm`, `label_size_max_mm`, `label_max_elements` ([RUDOLF] 02.11.2026, Zweit-Review #1739).
 */
export interface LabelDesignLimits {
  sizeMinMm: number;
  sizeMaxMm: number;
  maxElements: number;
}
/** Form eines Feldschlüssels: `<modul>.<feld>` (z. B. `catalog.number`, `catalog.prop_<id>`). */
export const LABEL_FIELD_KEY_PATTERN = /^[a-z][a-z0-9_]*\.\S+$/;

const SYSTEM_KEYS: ReadonlySet<string> = new Set(LABEL_SYSTEM_FIELDS.map((f) => f.key));
const RECEIPT_KEYS: ReadonlySet<string> = new Set(LABEL_RECEIPT_FIELDS.map((f) => f.key));
const EPS = 1e-6;

const keysOf = (schema: { shape: Record<string, unknown> }) => new Set(Object.keys(schema.shape));
const DESIGN_KEYS = keysOf(labelDesignSchema);
const SHEET_KEYS = keysOf(labelSheetLayoutSchema);
const ELEMENT_KEYS = new Map(labelElementSchema.options.map((o) => [o.shape.type.value as string, keysOf(o)]));
const SEGMENT_KEYS = new Map(labelSegmentSchema.options.map((o) => [o.shape.kind.value as string, keysOf(o)]));

const isRecord = (v: unknown): v is Record<string, unknown> => v !== null && typeof v === 'object' && !Array.isArray(v);

/** Unbekannte Angaben im rohen Design (das gemeinsame Schema würde sie still entfernen). */
function unknownPropertyErrors(raw: Record<string, unknown>): Record<string, string> {
  const errors: Record<string, string> = {};
  const check = (value: Record<string, unknown>, allowed: ReadonlySet<string> | undefined, path: string) => {
    if (!allowed) return;
    const extra = Object.keys(value).filter((k) => !allowed.has(k));
    if (extra.length > 0) errors[path || '_'] ??= LABEL_TEXTS.unknownProperty(extra);
  };
  check(raw, DESIGN_KEYS, '');
  if (!Array.isArray(raw.elements)) return errors;
  raw.elements.forEach((el, i) => {
    if (!isRecord(el)) return;
    check(el, ELEMENT_KEYS.get(String(el.type)), `elements.${i}`);
    for (const prop of ['segments', 'content']) {
      const list = el[prop];
      if (!Array.isArray(list)) continue;
      list.forEach((s, j) => {
        if (isRecord(s)) check(s, SEGMENT_KEYS.get(String(s.kind)), `elements.${i}.${prop}.${j}`);
      });
    }
  });
  return errors;
}

function valueAt(input: unknown, path: readonly PropertyKey[]): unknown {
  let cur = input;
  for (const p of path) {
    if (cur === null || typeof cur !== 'object') return undefined;
    cur = (cur as Record<PropertyKey, unknown>)[p];
  }
  return cur;
}

/** Ein Zod-Fehler als deutscher Satz. */
function issueText(issue: z.core.$ZodIssue, input: unknown): string {
  switch (issue.code) {
    case 'invalid_union': {
      // Unbekannte Art eines Elements oder Bausteins (Diskriminator passt nicht; Pfad endet auf `type` bzw. `kind`).
      const disc = (issue as { discriminator?: string }).discriminator;
      if (disc === 'type') return LABEL_TEXTS.elementUnknown(String(valueAt(input, issue.path)));
      if (disc === 'kind') return LABEL_TEXTS.segmentUnknown(String(valueAt(input, issue.path)));
      return LABEL_TEXTS.valueWrongType;
    }
    case 'unrecognized_keys':
      return LABEL_TEXTS.unknownProperty(issue.keys);
    case 'invalid_type':
      return issue.input === undefined ? LABEL_TEXTS.valueMissing : LABEL_TEXTS.valueWrongType;
    case 'invalid_value':
      return LABEL_TEXTS.valueChoice(issue.values.map((v) => String(v)));
    case 'too_small':
      if (issue.origin === 'string') return LABEL_TEXTS.textTooShort;
      if (issue.origin === 'array') return LABEL_TEXTS.listTooShort;
      return LABEL_TEXTS.valueTooSmall(issue.minimum as number);
    case 'too_big':
      if (issue.origin === 'string') return LABEL_TEXTS.textTooLong(issue.maximum as number);
      if (issue.origin === 'array') return LABEL_TEXTS.tooManyElements(issue.maximum as number);
      return LABEL_TEXTS.valueTooBig(issue.maximum as number);
    default:
      return LABEL_TEXTS.valueWrongType;
  }
}

/** Zod-Fehler → Pfad (`elements.3.type`, leer = `_`) → deutscher Satz (erster je Pfad). */
export function labelIssuesToErrors(issues: readonly z.core.$ZodIssue[], input: unknown): Record<string, string> {
  const errors: Record<string, string> = {};
  for (const issue of issues) {
    const field = issue.path.length > 0 ? issue.path.map(String).join('.') : '_';
    errors[field] ??= issueText(issue, input);
  }
  return errors;
}

/** Problem eines Feldschlüssels (Form und Namensraum der System-/Anlass-Felder); `null` = in Ordnung. */
export function labelFieldKeyProblem(key: string, kind: LabelTemplateKind): string | null {
  if (!LABEL_FIELD_KEY_PATTERN.test(key)) return LABEL_TEXTS.fieldKeyFormat;
  if (key.startsWith('system.')) return SYSTEM_KEYS.has(key) ? null : LABEL_TEXTS.fieldUnknown(key);
  if (key.startsWith('receipt.')) {
    if (!RECEIPT_KEYS.has(key)) return LABEL_TEXTS.fieldUnknown(key);
    return kind === 'article' ? null : LABEL_TEXTS.fieldOnlyArticle(key);
  }
  return null;
}

/** Segmente eines Elements (Text: `segments`, Strichcode: `content`). */
function segmentsOf(el: LabelDesign['elements'][number]): { prop: 'segments' | 'content'; list: readonly LabelSegment[] } | null {
  if (el.type === 'text') return { prop: 'segments', list: el.segments };
  if (el.type === 'barcode') return { prop: 'content', list: el.content };
  return null;
}

/** Pfade aller Feld-Segmente eines Designs mit Schlüssel (für Fehler am richtigen Baustein). */
export function fieldSegmentPaths(design: Pick<LabelDesign, 'elements'>): { key: string; path: string }[] {
  const out: { key: string; path: string }[] = [];
  design.elements.forEach((el, i) => {
    const seg = segmentsOf(el);
    seg?.list.forEach((s, j) => {
      if (s.kind === 'field') out.push({ key: s.key, path: `elements.${i}.${seg.prop}.${j}.key` });
    });
  });
  return out;
}

/** Alle Feldschlüssel eines Designs (Text-Segmente und Code-Inhalte), ohne Doppel, in Reihenfolge des Auftretens. */
export function collectFieldKeys(design: Pick<LabelDesign, 'elements'>): string[] {
  return [...new Set(fieldSegmentPaths(design).map((f) => f.key))];
}

const onTenth = (v: number) => Math.abs(v * 10 - Math.round(v * 10)) < 1e-6;

/** Größe in mm gegen die eingestellten Grenzen und das 0,1-mm-Raster; `null` = in Ordnung. */
export function labelSizeProblem(v: number, limits: Pick<LabelDesignLimits, 'sizeMinMm' | 'sizeMaxMm'>): string | null {
  if (v < limits.sizeMinMm) return LABEL_TEXTS.valueTooSmall(limits.sizeMinMm);
  if (v > limits.sizeMaxMm) return LABEL_TEXTS.valueTooBig(limits.sizeMaxMm);
  return onTenth(v) ? null : LABEL_TEXTS.stepTenth;
}

/**
 * Design gegen gemeinsames Schema, die Regeln des Speicherns, die Größe der Vorlage und den Namensraum der Felder
 * prüfen. Ergebnis mit Standardwerten (z. B. `rotation: 0`).
 */
export function checkLabelDesign(
  raw: unknown,
  template: { widthMm: number; heightMm: number; kind: LabelTemplateKind },
  limits: LabelDesignLimits,
): LabelCheckResult<LabelDesign> {
  if (!isRecord(raw)) return { ok: false, errors: { _: LABEL_TEXTS.designNotObject } };
  const parsed = labelDesignSchema.safeParse(raw);
  const errors: Record<string, string> = parsed.success ? {} : labelIssuesToErrors(parsed.error.issues, raw);
  for (const [k, v] of Object.entries(unknownPropertyErrors(raw))) errors[k] ??= v;
  if (!parsed.success || Object.keys(errors).length > 0) return { ok: false, errors };
  const design = parsed.data;
  for (const prop of ['widthMm', 'heightMm'] as const) {
    const problem = labelSizeProblem(design[prop], limits);
    if (problem) errors[prop] = problem;
  }
  if (design.elements.length > limits.maxElements) errors.elements = LABEL_TEXTS.tooManyElements(limits.maxElements);
  if (Object.keys(errors).length > 0) return { ok: false, errors };
  if (Math.abs(design.widthMm - template.widthMm) > EPS || Math.abs(design.heightMm - template.heightMm) > EPS) {
    errors._ = LABEL_TEXTS.sizeMismatch(template.widthMm, template.heightMm);
  }
  const seen = new Set<string>();
  design.elements.forEach((el, i) => {
    if (seen.has(el.id)) errors[`elements.${i}.id`] ??= LABEL_TEXTS.duplicateId;
    seen.add(el.id);
    if (el.x + el.w > design.widthMm + EPS || el.y + el.h > design.heightMm + EPS) errors[`elements.${i}`] ??= LABEL_TEXTS.outside(design.widthMm, design.heightMm);
  });
  for (const { key, path } of fieldSegmentPaths(design)) {
    const problem = labelFieldKeyProblem(key, template.kind);
    if (problem) errors[path] ??= problem;
  }
  return Object.keys(errors).length > 0 ? { ok: false, errors } : { ok: true, value: design };
}

/**
 * Feldschlüssel gegen einen Katalog bekannter Schlüssel prüfen (Server, Feld-Katalog aus dem Listen-Register).
 * Fehler je Segment-Pfad; leer = alle bekannt.
 */
export function unknownLabelFieldErrors(design: Pick<LabelDesign, 'elements'>, known: ReadonlySet<string>): Record<string, string> {
  const errors: Record<string, string> = {};
  for (const { key, path } of fieldSegmentPaths(design)) if (!known.has(key)) errors[path] ??= LABEL_TEXTS.fieldUnknown(key);
  return errors;
}

/** Bogen-Layout prüfen: Schema, keine unbekannten Angaben und „Etiketten passen auf den Bogen“. */
export function checkLabelSheetLayout(raw: unknown, label: { widthMm: number; heightMm: number }): LabelCheckResult<LabelSheetLayout> {
  if (!isRecord(raw)) return { ok: false, errors: { _: LABEL_TEXTS.sheetLayoutMissing } };
  const extra = Object.keys(raw).filter((k) => !SHEET_KEYS.has(k));
  if (extra.length > 0) return { ok: false, errors: { _: LABEL_TEXTS.unknownProperty(extra) } };
  const parsed = labelSheetLayoutSchema.safeParse(raw);
  if (!parsed.success) return { ok: false, errors: labelIssuesToErrors(parsed.error.issues, raw) };
  const s = parsed.data;
  const needW = s.marginLeftMm + s.columns * label.widthMm + (s.columns - 1) * s.gapXMm;
  const needH = s.marginTopMm + s.rows * label.heightMm + (s.rows - 1) * s.gapYMm;
  if (needW > s.pageWidthMm + EPS || needH > s.pageHeightMm + EPS) return { ok: false, errors: { _: LABEL_TEXTS.sheetLayoutTooSmall } };
  return { ok: true, value: s };
}
